<!-- App.svelte -->
<script>
  import { onMount } from 'svelte';
  import DiscountsList from './components/DiscountsList.svelte';
  import RecipeSuggestions from './components/RecipeSuggestions.svelte';
  import SearchFilters from './components/SearchFilters.svelte';
  import PdfDataParser from './components/PdfDataParser.svelte';
  import RecipeDetail from './components/RecipeDetail.svelte';
  import RecipeCreate from './components/RecipeCreate.svelte';
  
  import { 
    discountItems, 
    recipes, 
    selectedIngredients,
    searchTerm,
    selectedCategories,
    filteredDiscounts,
    suggestedRecipes,
    toggleIngredientSelection,
    isIngredientSelected,
    initializeStores
  } from './store.js';
  
  export let title;
  
  // State variables
  let showPdfParser = false;
  let selectedRecipe = null;
  let showRecipeCreate = false;
  
  // Get all available categories for filtering
  $: allCategories = [...new Set($discountItems.map(item => item.category))];
  
  // Initialize our data when the component mounts
  onMount(() => {
    initializeStores();
  });
  
  // Functions for handling user interactions
  function handleCategoryChange(event) {
    selectedCategories.set(event.detail);
  }
  
  function handleSearch(event) {
    searchTerm.set(event.detail);
  }

  function handleRecipeSelect(event) {
    selectedRecipe = event.detail;
  }

  function closeRecipeDetail() {
    selectedRecipe = null;
  }

  function togglePdfParser() {
    showPdfParser = !showPdfParser;
  }
  
  function openRecipeCreate() {
    showRecipeCreate = true;
  }
  
  function closeRecipeCreate() {
    showRecipeCreate = false;
  }
  
  function handleRecipeCreated(event) {
    closeRecipeCreate();
    // Could show a success message here
  }
</script>

<main>
  <header>
    <div class="logo">
      <span class="logo-text">Recept</span>
      <span class="logo-accent">Fynd</span>
    </div>
    <h1>{title}</h1>
    <p class="tagline">Hitta receptidéer baserade på veckans extrapriser</p>
  </header>
  
  <div class="app-container">
    <div class="sidebar">
      <div class="admin-controls">
        <button class="import-button" on:click={togglePdfParser}>
          {showPdfParser ? 'Dölj importverktyg' : 'Importera erbjudanden'}
        </button>
      </div>

      {#if showPdfParser}
        <PdfDataParser />
      {/if}

      <SearchFilters 
        categories={allCategories} 
        on:categoryChange={handleCategoryChange}
        on:search={handleSearch}
      />
      
      <div class="selected-ingredients">
        <h3>Valda råvaror ({$selectedIngredients.length})</h3>
        {#if $selectedIngredients.length > 0}
          <ul>
            {#each $selectedIngredients as item}
              <li>
                {item.name}
                <button on:click={() => toggleIngredientSelection(item)}>Ta bort</button>
              </li>
            {/each}
          </ul>
          
          <button class="clear-all-button" on:click={() => selectedIngredients.set([])}>
            Rensa alla
          </button>
        {:else}
          <p>Välj råvaror från listan för att få receptförslag</p>
        {/if}
      </div>
    </div>
    
    <div class="content">
      <div class="discounts-container">
        <h2>Veckans extrapriser</h2>
        <p class="discounts-info">Klicka på varor för att lägga till dem i din lista och få passande receptförslag.</p>
        
        <DiscountsList 
          discounts={$filteredDiscounts} 
          on:selectItem={e => toggleIngredientSelection(e.detail)}
          isSelected={isIngredientSelected}
        />
      </div>
      
      <div class="recipes-container">
        <div class="recipes-header">
          <h2>Receptförslag</h2>
          <button class="add-recipe-button" on:click={openRecipeCreate}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Skapa recept
          </button>
        </div>
        
        {#if $selectedIngredients.length > 0}
          <RecipeSuggestions 
            recipes={$suggestedRecipes} 
            on:selectRecipe={handleRecipeSelect}
          />
        {:else}
          <div class="empty-state">
            <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            <p>Välj råvaror från veckans extrapriser för att se receptförslag</p>
          </div>
        {/if}
      </div>
    </div>
  </div>

  {#if selectedRecipe}
    <RecipeDetail 
      recipe={selectedRecipe} 
      on:close={closeRecipeDetail} 
    />
  {/if}
  
  {#if showRecipeCreate}
    <RecipeCreate 
      on:close={closeRecipeCreate}
      on:created={handleRecipeCreated}
    />
  {/if}
</main>

<style>
  main {
    font-family: 'Poppins', sans-serif;
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
  }
  
  header {
    text-align: center;
    margin-bottom: 40px;
  }
  
  .logo {
    font-size: 28px;
    font-weight: bold;
    margin-bottom: 10px;
  }
  
  .logo-text {
    color: #333;
  }
  
  .logo-accent {
    color: #e91e63;
  }
  
  h1 {
    color: #333;
    margin: 10px 0;
    font-size: 24px;
  }
  
  .tagline {
    color: #666;
    margin: 0;
  }
  
  .app-container {
    display: flex;
    gap: 30px;
  }
  
  .sidebar {
    flex: 0 0 280px;
  }
  
  .content {
    flex: 1;
  }
  
  .discounts-container, .recipes-container {
    margin-bottom: 40px;
  }
  
  h2 {
    color: #333;
    border-bottom: 2px solid #e91e63;
    padding-bottom: 8px;
    margin-bottom: 20px;
    font-size: 20px;
  }
  
  .recipes-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }
  
  .recipes-header h2 {
    margin-bottom: 0;
  }
  
  .add-recipe-button {
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: #4caf50;
    color: white;
    border: none;
    padding: 8px 15px;
    border-radius: 5px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .add-recipe-button:hover {
    background-color: #3f9142;
  }
  
  h3 {
    color: #333;
    margin-bottom: 15px;
    font-size: 16px;
  }
  
  .discounts-info {
    color: #666;
    margin-bottom: 20px;
    font-size: 14px;
  }
  
  .selected-ingredients {
    margin-top: 30px;
    padding: 20px;
    background-color: #f8f8f8;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  .selected-ingredients ul {
    list-style-type: none;
    padding: 0;
    margin: 0 0 15px 0;
  }
  
  .selected-ingredients li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #eee;
  }
  
  .selected-ingredients li:last-child {
    border-bottom: none;
  }
  
  .selected-ingredients button {
    background-color: #ff6b6b;
    border: none;
    color: white;
    padding: 3px 8px;
    border-radius: 3px;
    cursor: pointer;
    font-size: 12px;
  }
  
  .clear-all-button {
    width: 100%;
    padding: 8px;
    background-color: #eee;
    border: none;
    border-radius: 4px;
    color: #333;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .clear-all-button:hover {
    background-color: #ddd;
  }
  
  .empty-state {
    text-align: center;
    padding: 40px 20px;
    background-color: #f9f9f9;
    border-radius: 8px;
    color: #666;
  }
  
  .empty-state svg {
    color: #ddd;
    margin-bottom: 20px;
  }

  .admin-controls {
    margin-bottom: 20px;
  }

  .import-button {
    width: 100%;
    padding: 10px;
    background-color: #333;
    color: white;
    border: none;
    border-radius: 4px;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .import-button:hover {
    background-color: #555;
  }
  
  @media (max-width: 768px) {
    .app-container {
      flex-direction: column;
    }
    
    .sidebar {
      flex: auto;
      width: 100%;
    }
  }
</style>